<div
  class="soft-customer-info-panel flex flex-col h-full bg-surface overflow-hidden"
  [class.border]="!flush()"
  [class.rounded-2xl]="!flush()"
>
  <div class="soft-customer-info-panel__header shrink-0">
    <div class="w-full p-2 border-b border-default pb-0">
      <div class="flex items-start justify-between gap-3 w-full">
        <div class="flex items-center gap-3 min-w-0 p-3">
          @if (customerDetailLoading()) {
            <soft-loading-skeleton width="48px" height="48px" radius="9999px" />
            <div class="flex flex-col min-w-0 gap-1.5">
              <soft-loading-skeleton width="70px" height="12px" />
              <soft-loading-skeleton width="180px" height="18px" />
              <soft-loading-skeleton width="110px" height="12px" />
            </div>
          } @else if (customerDetailError()) {
            <soft-ds-avatar size="2xl" />
            <div class="flex flex-col min-w-0 gap-0.5">
              <span class="text-lg font-semibold text-light">—</span>
            </div>
          } @else {
            <soft-ds-avatar size="2xl" [initials]="customerInitials()" />
            <div class="flex flex-col min-w-0 gap-0.5">
              <span class="text-light text-sm">
                Nr. {{ customerData()?.number }}
                @if (customerData()?.subnumber) {
                  /{{ customerData()?.subnumber }}
                }
              </span>
              <span class="text-lg font-semibold">
                {{ customerData()?.salutation }}
                {{ customerData()?.name1 }}
                {{ customerData()?.name2 }}
                {{ customerData()?.nameAffix }}
              </span>
              @if (customerGroupLabel(); as group) {
                <div class="flex items-center gap-1.5 ds-caption text-light">
                  <soft-ds-icon name="fa-regular fa-layer-group" size="xs" />
                  <span class="">{{ group }}</span>
                </div>
              }
            </div>
          }
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <soft-ds-button
            variant="ghost"
            size="md"
            mode="iconOnly"
            (click)="closePanel.emit()"
          >
            <soft-ds-icon *softSlot="'leading'" name="fa-regular fa-xmark" />
          </soft-ds-button>
        </div>
      </div>

      <!-- Status badge row -->
      <div class="flex flex-wrap items-center gap-2 pl-3 mt-1">
        @if (customerData()?.category; as category) {
          <soft-ds-badge size="md" variant="secondary">
            {{ category }}
          </soft-ds-badge>
        }
        @if (customerState(); as state) {
          @if (state.deleted) {
            <soft-ds-badge size="md" variant="destructive">
              <soft-ds-icon name="fa-regular fa-trash-can" size="xs" />
              Gelöscht
            </soft-ds-badge>
          }
          @if (state.inactive) {
            <soft-ds-badge size="md" variant="warning">
              <soft-ds-icon name="fa-regular fa-pause" size="xs" />
              Inaktiv
            </soft-ds-badge>
          } @else {
            <soft-ds-badge size="md" variant="success">
              <soft-ds-icon name="fa-regular fa-check" size="xs" />
              Aktiv
            </soft-ds-badge>
          }
          @if (state.blocked) {
            <soft-ds-badge size="md" variant="danger">
              <soft-ds-icon name="fa-regular fa-lock" size="xs" />
              Gesperrt
            </soft-ds-badge>
          } @else {
            <soft-ds-badge size="md" variant="secondary">
              <soft-ds-icon name="fa-regular fa-lock-open" size="xs" />
              Nicht gesperrt
            </soft-ds-badge>
          }
        }
        @for (ind of customerMasterData()?.indicators ?? []; track ind.index) {
          <soft-ds-badge size="md" variant="info">
            <soft-ds-icon name="fa-regular fa-flag" size="xs" />
            {{ ind.value }}
          </soft-ds-badge>
        }
      </div>

      <soft-ds-tabs
        variant="underline"
        size="sm"
        layout="scroll"
        [(value)]="panelTabIndex"
        class="sales-customer-tabs overflow-x-auto"
      >
        <soft-ds-tab [value]="0">Übersicht</soft-ds-tab>
        @if (!isOneTimeCustomer()) {
          <soft-ds-tab [value]="1" [badge]="constructionSitesTotal() || null">
            Baustellen
          </soft-ds-tab>
        }
        @if (!customerDetailLoading() && !customerDetailError()) {
          @if (customerContacts().length) {
            <soft-ds-tab [value]="2" [badge]="customerContacts().length">
              Kontakte
            </soft-ds-tab>
          }
          @if (customerConditions().length) {
            <soft-ds-tab [value]="3" [badge]="customerConditions().length">
              Konditionen
            </soft-ds-tab>
          }
          @if (customerCards().length) {
            <soft-ds-tab [value]="4" [badge]="customerCards().length">Karten</soft-ds-tab>
          }
          @if (customerFuelCards().length) {
            <soft-ds-tab [value]="5" [badge]="customerFuelCards().length">
              Tankkarten
            </soft-ds-tab>
          }
          @if (customerCollectors().length) {
            <soft-ds-tab [value]="6" [badge]="customerCollectors().length">
              Abholer
            </soft-ds-tab>
          }
          @if (customerNotes().length) {
            <soft-ds-tab [value]="7" [badge]="customerNotes().length">
              Notizen
            </soft-ds-tab>
          }
        }
      </soft-ds-tabs>
    </div>
  </div>
  <div
    class="soft-customer-info-panel__body flex-1 min-h-0 overflow-y-auto"
    (scroll)="onPanelBodyScroll($event)"
  >
    @if (customerDetailLoading()) {
      <div class="flex flex-col gap-3 p-4">
        <soft-loading-skeleton height="20px" />
        <soft-loading-skeleton height="20px" width="60%" />
        <soft-loading-skeleton height="16px" />
        <soft-loading-skeleton height="16px" width="80%" />
        <soft-loading-skeleton height="16px" width="70%" />
        <soft-loading-skeleton height="16px" />
        <soft-loading-skeleton height="16px" width="55%" />
        <soft-loading-skeleton height="16px" />
        <soft-loading-skeleton height="16px" width="75%" />
      </div>
    } @else if (customerDetailError()) {
      <div class="flex items-center justify-center h-full p-6">
        <soft-ds-empty-state
          variant="error"
          icon="fa-regular fa-triangle-exclamation"
          title="Kundendaten nicht verfügbar"
          description="Die Kundendaten konnten nicht geladen werden. Bitte versuchen Sie es erneut."
          actionLabel="Erneut versuchen"
          (action)="retryLoad()"
        />
      </div>
    } @else {
      <div class="sales-customer-panel flex flex-col gap-5 p-4">
        @if (panelTabIndex() === 0) {
          <!-- Kontakt -->
          <div class="sales-collapsible" [class.is-open]="isSectionExpanded('contact')">
            <div class="sales-collapsible__head">
              <button
                type="button"
                class="sales-collapsible__toggle"
                (click)="toggleSection('contact')"
              >
                <span class="sales-card-icon sm is-info">
                  <soft-ds-icon name="fa-regular fa-address-book" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">Kontakt</span>
              </button>
              <div class="flex items-center gap-1.5 shrink-0">
                @if (customerData()?.phone1; as phone) {
                  <a
                    [href]="'tel:' + phone"
                    class="sales-contact-action is-primary"
                    title="Anrufen"
                  >
                    <soft-ds-icon name="fa-regular fa-phone" size="sm" />
                  </a>
                }
                @if (customerData()?.email; as email) {
                  <a
                    [href]="'mailto:' + email"
                    class="sales-contact-action"
                    title="E-Mail senden"
                  >
                    <soft-ds-icon name="fa-regular fa-envelope" size="sm" />
                  </a>
                }
              </div>
              <button
                type="button"
                class="sales-collapsible__chevron-btn"
                (click)="toggleSection('contact')"
                aria-label="Ein-/ausklappen"
              >
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('contact')"
                />
              </button>
            </div>
            @if (isSectionExpanded('contact')) {
              <div class="sales-collapsible__body mt-2">
                @if (!contactHasData()) {
                  <p class="text-sm text-light">Keine Kontaktdaten hinterlegt.</p>
                }
                <!-- Telefonie -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  @if (customerData()?.phone1; as phone) {
                    <div class="flex items-center gap-2.5 min-w-0">
                      <soft-ds-icon
                        name="fa-regular fa-phone"
                        size="sm"
                        color="secondary"
                      />
                      <div class="flex flex-col min-w-0">
                        <a [href]="'tel:' + phone" class="soft-link">
                          {{ phone }}
                        </a>
                        <span class="ds-caption text-light">Telefon</span>
                      </div>
                    </div>
                  }
                  @if (customerData()?.phone2; as mobile) {
                    <div class="flex items-center gap-2.5 min-w-0">
                      <soft-ds-icon
                        name="fa-regular fa-mobile"
                        size="sm"
                        color="secondary"
                      />
                      <div class="flex flex-col min-w-0">
                        <a [href]="'tel:' + mobile" class="soft-link">
                          {{ mobile }}
                        </a>
                        <span class="ds-caption text-light">Mobil</span>
                      </div>
                    </div>
                  }
                  @if (customerData()?.directDial; as directDial) {
                    <div class="flex items-center gap-2.5 min-w-0">
                      <soft-ds-icon
                        name="fa-regular fa-phone-volume"
                        size="sm"
                        color="secondary"
                      />
                      <div class="flex flex-col min-w-0">
                        <span class="">{{ directDial }}</span>
                        <span class="ds-caption text-light">
                          Durchwahl
                          @if (customerData()?.phoneExtension; as ext) {
                            · {{ ext }}
                          }
                        </span>
                      </div>
                    </div>
                  }
                  @if (customerData()?.fax; as fax) {
                    <div class="flex items-center gap-2.5 min-w-0">
                      <soft-ds-icon
                        name="fa-regular fa-fax"
                        size="sm"
                        color="secondary"
                      />
                      <div class="flex flex-col min-w-0">
                        <span class="">{{ fax }}</span>
                        <span class="ds-caption text-light">Fax</span>
                      </div>
                    </div>
                  }
                </div>

                <!-- E-Mail & Web -->
                @if (
                  customerData()?.email ||
                  customerData()?.email2 ||
                  customerData()?.email3 ||
                  customerData()?.url
                ) {
                  <div
                    class="flex flex-col gap-2 mt-4 pt-4 border-t border-default text-sm"
                  >
                    @if (customerData()?.email; as email) {
                      <div class="flex items-center gap-2.5 min-w-0">
                        <soft-ds-icon
                          name="fa-regular fa-at"
                          size="sm"
                          color="secondary"
                        />
                        <a [href]="'mailto:' + email" class="soft-link">
                          {{ email }}
                        </a>
                        <span class="ds-caption text-light shrink-0">Zentrale</span>
                      </div>
                    }
                    @if (customerData()?.email2; as mail2) {
                      <div class="flex items-center gap-2.5 min-w-0">
                        <soft-ds-icon
                          name="fa-regular fa-at"
                          size="sm"
                          color="secondary"
                        />
                        <a [href]="'mailto:' + mail2" class="soft-link">
                          {{ mail2 }}
                        </a>
                        <span class="ds-caption text-light shrink-0">Buchhaltung</span>
                      </div>
                    }
                    @if (customerData()?.email3; as mail3) {
                      <div class="flex items-center gap-2.5 min-w-0">
                        <soft-ds-icon
                          name="fa-regular fa-at"
                          size="sm"
                          color="secondary"
                        />
                        <a [href]="'mailto:' + mail3" class="soft-link">
                          {{ mail3 }}
                        </a>
                      </div>
                    }
                    @if (customerData()?.url; as url) {
                      <div class="flex items-center gap-2.5 min-w-0">
                        <soft-ds-icon
                          name="fa-regular fa-globe"
                          size="sm"
                          color="secondary"
                        />
                        <a [href]="url" target="_blank" rel="noopener" class="soft-link">
                          {{ url }}
                        </a>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
          <!-- Adressen -->
          <div class="sales-collapsible" [class.is-open]="isSectionExpanded('addresses')">
            <button
              type="button"
              class="sales-collapsible__head"
              (click)="toggleSection('addresses')"
            >
              <span class="sales-card-icon sm is-info">
                <soft-ds-icon name="fa-regular fa-location-dot" size="xs" />
              </span>
              <span class="flex-1 ds-body-2-semibold text-left">Adressen</span>
              <span class="ds-caption text-light mr-1">
                {{ customerDeliveryAddress() ? 2 : 1 }}
              </span>
              <soft-ds-icon
                name="fa-regular fa-chevron-right"
                size="xs"
                class="sales-chevron"
                [class.is-open]="isSectionExpanded('addresses')"
              />
            </button>
            @if (isSectionExpanded('addresses')) {
              <div class="sales-collapsible__body">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="sales-addr min-w-0">
                    <div class="min-w-0">
                      <p class="sales-addr__label ds-caption text-light">
                        Rechnungsadresse
                      </p>
                      <div class="flex flex-col gap-0.5 text-sm mt-1">
                        <span>{{ customerData()?.street }}</span>
                        <span>
                          {{ customerData()?.postCode }} {{ customerData()?.city }}
                          @if (customerData()?.countryCode) {
                            ({{ customerData()?.countryCode }})
                          }
                        </span>
                        @if (customerMapsUrl(); as mapsUrl) {
                          <a
                            [href]="mapsUrl"
                            target="_blank"
                            rel="noopener"
                            class="soft-link ds-caption inline-flex items-center gap-1.5 mt-0.5"
                            title="In Google Maps öffnen"
                          >
                            <soft-ds-icon
                              name="fa-regular fa-map-location-dot"
                              size="xs"
                            />
                            <span>In Maps öffnen</span>
                          </a>
                        }
                      </div>
                    </div>
                  </div>

                  @if (customerDeliveryAddress(); as da) {
                    <div class="sales-addr min-w-0">
                      <div class="min-w-0">
                        <p class="sales-addr__label ds-caption text-light">
                          Lieferadresse
                        </p>
                        <div class="flex flex-col gap-0.5 text-sm mt-1">
                          @if (da.name1) {
                            <span>{{ da.name1 }} {{ da.name2 }} {{ da.name3 }}</span>
                          }
                          @if (da.street) {
                            <span>{{ da.street }}</span>
                          }
                          @if (da.postCode || da.city) {
                            <span>
                              {{ da.postCode }} {{ da.city }}
                              @if (da.countryCode) {
                                ({{ da.countryCode }})
                              }
                            </span>
                          }
                          @if (deliveryMapsUrl(); as mapsUrl) {
                            <a
                              [href]="mapsUrl"
                              target="_blank"
                              rel="noopener"
                              class="soft-link ds-caption inline-flex items-center gap-1.5 mt-0.5"
                              title="In Google Maps öffnen"
                            >
                              <soft-ds-icon
                                name="fa-regular fa-map-location-dot"
                                size="xs"
                              />
                              <span>In Maps öffnen</span>
                            </a>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Letzte Belege — temporarily removed (mock data) until wired to the
           customer recent-documents query. -->

          @if (customerMasterData(); as s) {
            <soft-credit-limit-card
              [customer]="customer()"
              [documentId]="documentId()"
              [documentRevision]="documentRevision()"
            />

            @if (!isOneTimeCustomer()) {
            <!-- KPI tiles — only where the Auswertungsdatenbank answers -->
            @if (customerStatisticsAvailable()) {
            <div class="grid grid-cols-2 gap-3" data-testid="customer-statistics-kpis">
              <soft-ds-kpi
                title="Umsatz YTD"
                density="compact"
                [value]="customerRevenue()"
                [prevValue]="priorYearRevenue()"
                format="currency"
                currencyCode="EUR"
                currencySymbol="symbol"
                variant="sparkline"
                comparisonMode="delta"
                comparisonLabel="ggü. Vorjahr"
                comparisonPosition="below"
                [sparkline]="customerRevenueSparkline()"
                [loading]="customerStatisticsLoading()"
              />
              <soft-ds-kpi
                title="Deckungsbeitrag"
                density="compact"
                [value]="customerDb()"
                [prevValue]="priorYearDb()"
                format="currency"
                currencyCode="EUR"
                currencySymbol="symbol"
                variant="sparkline"
                comparisonMode="delta"
                comparisonLabel="ggü. Vorjahr"
                comparisonPosition="below"
                [sparkline]="customerDbSparkline()"
                [loading]="customerStatisticsLoading()"
              />
            </div>
            }

            } @else {
              <div class="sales-onetime-hint" data-testid="one-time-customer-hint">
                <soft-ds-icon name="fa-regular fa-circle-info" size="xs" />
                <span class="ds-body-2">
                  Barverkaufskunde — Sammelkonto für Laufkundschaft. Umsatzkennzahlen
                  und Baustellen werden dafür nicht ausgewertet.
                </span>
              </div>
            }

            <!-- Konditionen & Zahlung -->
            <div
              class="sales-collapsible"
              [class.is-open]="isSectionExpanded('conditions')"
            >
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('conditions')"
              >
                <span class="sales-card-icon sm is-success">
                  <soft-ds-icon name="fa-regular fa-tags" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">
                  Konditionen &amp; Zahlung
                </span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('conditions')"
                />
              </button>
              @if (isSectionExpanded('conditions')) {
                <div class="sales-collapsible__body">
                  <div class="sales-row">
                    <span class="sales-row__label">Zahlungskondition</span>
                    <span class="sales-row__value">{{ s.termsOfPayment }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Fälligkeit</span>
                    <span class="sales-row__value">
                      @if (s.termsDueDays !== null) {
                        {{ s.termsDueDays }} Tage
                      } @else {
                        —
                      }
                    </span>
                  </div>
                  <div class="flex gap-2.5 py-2">
                    <div class="sales-box">
                      <div class="sales-box__label ds-caption text-light">
                        <soft-ds-icon name="fa-regular fa-percent" size="xs" />
                        Skonto 1
                      </div>
                      <div class="sales-box__value">
                        {{
                          s.termsCashDiscount1 !== null
                            ? (s.termsCashDiscount1 | number: '1.2-2') + ' %'
                            : '—'
                        }}
                        @if (s.termsDays1 !== null && s.termsDays1 !== 0) {
                          / {{ s.termsDays1 }}T
                        }
                      </div>
                    </div>
                    <div class="sales-box">
                      <div class="sales-box__label ds-caption text-light">
                        <soft-ds-icon name="fa-regular fa-percent" size="xs" />
                        Skonto 2
                      </div>
                      <div class="sales-box__value">
                        {{
                          s.termsCashDiscount2 !== null
                            ? (s.termsCashDiscount2 | number: '1.2-2') + ' %'
                            : '—'
                        }}
                        @if (s.termsDays2 !== null && s.termsDays2 !== 0) {
                          / {{ s.termsDays2 }}T
                        }
                      </div>
                    </div>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Preisstufe</span>
                    <span class="sales-row__value">{{ s.priceLevel }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Konditionsblatt</span>
                    <span class="sales-row__value">{{ s.conditionSheet }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Zahlart 1</span>
                    <span class="sales-row__value">{{ s.paymentMethod }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Zahlart 2</span>
                    <span class="sales-row__value">{{ s.paymentMethod2 }}</span>
                  </div>
                  <div class="sales-row is-last">
                    <span class="sales-row__label">Zahlungsintervall</span>
                    <span class="sales-row__value">{{ s.paymentInterval }}</span>
                  </div>
                </div>
              }
            </div>

            <!-- Vertreter -->
            <div
              class="sales-collapsible"
              [class.is-open]="isSectionExpanded('representatives')"
            >
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('representatives')"
              >
                <span class="sales-card-icon sm">
                  <soft-ds-icon name="fa-regular fa-user-tie" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">Vertreter</span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('representatives')"
                />
              </button>
              @if (isSectionExpanded('representatives')) {
                <div class="sales-collapsible__body">
                  <div class="flex flex-col gap-2.5">
                    <div class="flex items-center gap-3">
                      <span class="sales-avatar">
                        {{ initialsOf(s.representative1) }}
                      </span>
                      <div class="min-w-0">
                        <div class="ds-body-2-medium">
                          {{ s.representative1 }}
                        </div>
                        <div class="ds-caption text-light">Vertreter 1</div>
                      </div>
                    </div>
                    <div class="flex items-center gap-3">
                      <span class="sales-avatar">
                        {{ initialsOf(s.representative2) }}
                      </span>
                      <div class="min-w-0">
                        <div class="ds-body-2-medium">
                          {{ s.representative2 }}
                        </div>
                        <div class="ds-caption text-light">Vertreter 2</div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>

            <!-- Kundengruppe -->
            <div
              class="sales-collapsible"
              [class.is-open]="isSectionExpanded('customerGroup')"
            >
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('customerGroup')"
              >
                <span class="sales-card-icon sm">
                  <soft-ds-icon name="fa-regular fa-layer-group" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">Kundengruppe</span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('customerGroup')"
                />
              </button>
              @if (isSectionExpanded('customerGroup')) {
                <div class="sales-collapsible__body">
                  <div class="sales-row">
                    <span class="sales-row__label">Hauptgruppe</span>
                    <span class="sales-row__value">
                      {{ customerData()?.mainGroup || '—' }}
                    </span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Untergruppe</span>
                    <span class="sales-row__value">
                      {{ customerData()?.subGroup || '—' }}
                    </span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Untergruppe 1</span>
                    <span class="sales-row__value">
                      {{ customerData()?.subGroup1 || '—' }}
                    </span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Gruppe Preisstufe</span>
                    <span class="sales-row__value">{{ s.groupPriceLevel }}</span>
                  </div>
                  <div class="sales-row is-last">
                    <span class="sales-row__label">Gruppe Konditionsblatt</span>
                    <span class="sales-row__value">{{ s.groupConditionSheet }}</span>
                  </div>
                </div>
              }
            </div>

            <!-- Steuer & EDI -->
            <div class="sales-collapsible" [class.is-open]="isSectionExpanded('tax')">
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('tax')"
              >
                <span class="sales-card-icon sm is-warning">
                  <soft-ds-icon name="fa-regular fa-percent" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">Steuer &amp; EDI</span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('tax')"
                />
              </button>
              @if (isSectionExpanded('tax')) {
                <div class="sales-collapsible__body">
                  <div class="sales-row">
                    <span class="sales-row__label">UID</span>
                    <span class="sales-row__value">{{ s.taxVatId }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Landwirt-Betriebsnr.</span>
                    <span class="sales-row__value">
                      {{ s.agriculturalBusinessNumber }}
                    </span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Edifact-Gruppe 1</span>
                    <span class="sales-row__value">{{ s.edifactGroup }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Edifact-Gruppe 2</span>
                    <span class="sales-row__value">{{ s.edifactGroup2 }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">GLN</span>
                    <span class="sales-row__value">{{ s.gln }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">GLN Zentrale</span>
                    <span class="sales-row__value">{{ s.glnCentral }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Bestell-BBN</span>
                    <span class="sales-row__value">{{ s.orderBbn }}</span>
                  </div>
                  <div class="sales-row is-last">
                    <span class="sales-row__label">Bestell-BBN 13</span>
                    <span class="sales-row__value">{{ s.orderBbn13 }}</span>
                  </div>
                </div>
              }
            </div>

            <!-- Lieferung & Belege -->
            <div
              class="sales-collapsible"
              [class.is-open]="isSectionExpanded('delivery')"
            >
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('delivery')"
              >
                <span class="sales-card-icon sm is-success">
                  <soft-ds-icon name="fa-regular fa-truck" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">
                  Lieferung &amp; Belege
                </span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('delivery')"
                />
              </button>
              @if (isSectionExpanded('delivery')) {
                <div class="sales-collapsible__body">
                  <div class="sales-row">
                    <span class="sales-row__label">Liefernummer</span>
                    <span class="sales-row__value">{{ s.deliveryNumber }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Lieferschein-Druck</span>
                    <span class="sales-row__value">{{ s.deliveryNotePrint }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">LS-Faktura-Einst.</span>
                    <span class="sales-row__value">
                      {{ s.deliveryNotesInvoicingSettings }}
                    </span>
                  </div>
                  <div class="sales-row is-last">
                    <span class="sales-row__label">Externe Bestellnr.</span>
                    <span class="sales-row__value">
                      {{ s.externalPurchaseOrderNumber }}
                    </span>
                  </div>
                </div>
              }
            </div>

            <!-- Versicherung & Einkauf -->
            <div
              class="sales-collapsible"
              [class.is-open]="isSectionExpanded('insurance')"
            >
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('insurance')"
              >
                <span class="sales-card-icon sm">
                  <soft-ds-icon name="fa-regular fa-shield-check" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">
                  Versicherung &amp; Einkauf
                </span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('insurance')"
                />
              </button>
              @if (isSectionExpanded('insurance')) {
                <div class="sales-collapsible__body">
                  <div class="sales-row">
                    <span class="sales-row__label">Versicherungs-Kz.</span>
                    <span class="sales-row__value">{{ s.insuranceIndicator }}</span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Versicherungs-Kz. 2</span>
                    <span class="sales-row__value">{{ s.insuranceIndicator2 }}</span>
                  </div>
                  <div class="sales-row is-last">
                    <span class="sales-row__label">Einkauf-Lieferant</span>
                    <span class="sales-row__value">{{ s.purchaseSupplier }}</span>
                  </div>
                </div>
              }
            </div>

            @if (s.summations.length > 0 || s.terms.length > 0) {
              <div
                class="sales-collapsible"
                [class.is-open]="isSectionExpanded('totals')"
              >
                <button
                  type="button"
                  class="sales-collapsible__head"
                  (click)="toggleSection('totals')"
                >
                  <span class="sales-card-icon sm is-info">
                    <soft-ds-icon name="fa-regular fa-sigma" size="xs" />
                  </span>
                  <span class="flex-1 ds-body-2-semibold text-left">
                    Summen &amp; Kennzahlen
                  </span>
                  <soft-ds-icon
                    name="fa-regular fa-chevron-right"
                    size="xs"
                    class="sales-chevron"
                    [class.is-open]="isSectionExpanded('totals')"
                  />
                </button>
                @if (isSectionExpanded('totals')) {
                  <div class="sales-collapsible__body">
                    @if (s.summations.length > 0) {
                      <div class="grid grid-cols-2 gap-2.5">
                        @for (sm of s.summations; track sm.index) {
                          <div class="sales-box">
                            <div class="sales-box__label ds-caption text-light">
                              <soft-ds-icon name="fa-regular fa-sigma" size="xs" />
                              Summe {{ sm.index }}
                            </div>
                            <div class="sales-box__value">
                              {{ sm.value | number: '1.2-2' }}
                            </div>
                          </div>
                        }
                      </div>
                    }
                    @if (s.terms.length > 0) {
                      <div class="flex flex-col mt-3">
                        @for (tm of s.terms; track tm.index) {
                          <div class="sales-row" [class.is-last]="$last">
                            <span class="sales-row__label">Wert {{ tm.index }}</span>
                            <span class="sales-row__value">{{ tm.value }}</span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <!-- Sonstiges -->
            <div class="sales-collapsible" [class.is-open]="isSectionExpanded('other')">
              <button
                type="button"
                class="sales-collapsible__head"
                (click)="toggleSection('other')"
              >
                <span class="sales-card-icon sm">
                  <soft-ds-icon name="fa-regular fa-ellipsis" size="xs" />
                </span>
                <span class="flex-1 ds-body-2-semibold text-left">Sonstiges</span>
                <soft-ds-icon
                  name="fa-regular fa-chevron-right"
                  size="xs"
                  class="sales-chevron"
                  [class.is-open]="isSectionExpanded('other')"
                />
              </button>
              @if (isSectionExpanded('other')) {
                <div class="sales-collapsible__body">
                  <div class="sales-row">
                    <span class="sales-row__label">Subnummer</span>
                    <span class="sales-row__value">
                      {{ customerData()?.subnumber ?? '—' }}
                    </span>
                  </div>
                  <div class="sales-row">
                    <span class="sales-row__label">Lieferkunden vorhanden</span>
                    <span class="sales-row__value">
                      {{ customerDeliveryCustomersExist() ? 'Ja' : 'Nein' }}
                    </span>
                  </div>
                  <div class="sales-row is-last">
                    <span class="sales-row__label">Belegart</span>
                    <span class="sales-row__value">
                      {{ customerDocumentType() ?? '—' }}
                    </span>
                  </div>
                </div>
              }
            </div>
          }
        }

        <!-- Baustellen -->
        @if (panelTabIndex() === 1 && !isOneTimeCustomer()) {
          <!-- Header + "Baustelle erstellen" ALWAYS shows; the search field appears
             only when there are sites to filter. -->
          <div class="sales-bv-sticky flex flex-col gap-2">
            <div class="flex items-center justify-between px-0.5">
              <span class="sales-section-label" style="margin-bottom: 0">
                @if (constructionSitesTotal(); as total) {
                  {{ total }} Baustelle{{ total === 1 ? '' : 'n' }}
                } @else {
                  Baustellen
                }
              </span>
              <button
                type="button"
                class="soft-link inline-flex items-center gap-1.5"
                (click)="createConstructionSite()"
                data-testid="create-construction-site"
              >
                <soft-ds-icon name="fa-regular fa-plus" size="xs" />
                <span class="ds-body-2-medium">Baustelle erstellen</span>
              </button>
            </div>
            @if (customerConstructionSites().length || siteSearch()) {
              <soft-ds-search-input
                placeholder="Baustellen durchsuchen…"
                [loading]="constructionSitesLoading()"
                loadingPlacement="trailing"
                [(value)]="siteSearch"
              />
            }
          </div>
          @if (constructionSitesLoading()) {
            <!-- Skeleton mirrors the site rows: thumb square + number/name/address lines. -->
            <div class="flex flex-col gap-2">
              @for (row of [1, 2, 3, 4]; track row) {
                <div class="sales-bv-item sales-bv-item--skeleton">
                  <soft-loading-skeleton
                    width="2.75rem"
                    height="2.75rem"
                    radius="var(--ds-radius-md)"
                  />
                  <span class="sales-bv-item__content gap-1.5">
                    <soft-loading-skeleton width="3.5rem" height="12px" radius="4px" />
                    <soft-loading-skeleton width="70%" height="14px" radius="4px" />
                    <soft-loading-skeleton width="55%" height="12px" radius="4px" />
                  </span>
                </div>
              }
            </div>
          } @else if (constructionSitesError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Baustellen konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryConstructionSites()"
            />
          } @else if (customerConstructionSites().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              [description]="
                siteSearch()
                  ? 'Keine Baustellen gefunden.'
                  : 'Keine Baustellen vorhanden.'
              "
            />
          } @else {
            <div class="flex flex-col gap-2">
              @for (
                site of customerConstructionSites();
                track site.id;
                let i = $index
              ) {
                <button
                  type="button"
                  class="sales-bv-item"
                  (click)="createDocumentForSite.emit(site)"
                >
                  <soft-site-map-thumb [index]="i" />
                  <span class="sales-bv-item__content">
                    <span class="flex items-center gap-2">
                      <span class="sales-bv-item__nr">
                        {{ site.customerNumber }}/{{ site.customerSubnumber }}
                      </span>
                    </span>
                    <span class="ds-body-2-semibold truncate">
                      {{ constructionSiteName(site) }}
                    </span>
                    @if (constructionSiteAddress(site); as addr) {
                      <span
                        class="ds-caption text-light inline-flex items-center gap-1.5 truncate"
                      >
                        <soft-ds-icon name="fa-regular fa-location-dot" size="xs" />
                        <span class="truncate">{{ addr }}</span>
                      </span>
                    }
                  </span>
                  @if (constructionSiteMapsUrl(site)) {
                    <span
                      role="button"
                      tabindex="0"
                      class="sales-bv-item__map"
                      title="In Google Maps öffnen"
                      aria-label="In Google Maps öffnen"
                      (click)="openSiteInMaps(site, $event)"
                      (keydown.enter)="openSiteInMaps(site, $event)"
                      (keydown.space)="openSiteInMaps(site, $event)"
                    >
                      <soft-ds-icon name="fa-regular fa-map-location-dot" size="sm" />
                    </span>
                  }
                  <span
                    role="button"
                    tabindex="0"
                    class="sales-bv-item__map"
                    title="Baustelle ändern"
                    aria-label="Baustelle ändern"
                    data-testid="edit-construction-site"
                    (click)="editConstructionSite(site, $event)"
                    (keydown.enter)="editConstructionSite(site, $event)"
                    (keydown.space)="editConstructionSite(site, $event)"
                  >
                    <soft-ds-icon name="fa-regular fa-pen" size="sm" />
                  </span>
                  <soft-ds-icon
                    name="fa-regular fa-chevron-right"
                    size="xs"
                    class="sales-bv-item__chevron"
                  />
                </button>
              }
              @if (constructionSitesLoadingMore()) {
                <div class="sales-bv-item sales-bv-item--skeleton">
                  <soft-loading-skeleton
                    width="2.75rem"
                    height="2.75rem"
                    radius="var(--ds-radius-md)"
                  />
                  <span class="sales-bv-item__content gap-1.5">
                    <soft-loading-skeleton width="3.5rem" height="12px" radius="4px" />
                    <soft-loading-skeleton width="70%" height="14px" radius="4px" />
                    <soft-loading-skeleton width="55%" height="12px" radius="4px" />
                  </span>
                </div>
              }
            </div>
          }
        }

        <!-- Kontakte -->
        @if (panelTabIndex() === 2) {
          @if (customerContactsLoading()) {
            <p class="text-sm text-light">Lade Kontakte…</p>
          } @else if (customerContactsError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Kontakte konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryLoad()"
            />
          } @else if (customerContacts().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              description="Keine Kontakte gefunden."
            />
          } @else {
            <div class="flex flex-col gap-3">
              @for (c of customerContacts(); track c.id) {
                <soft-ds-card>
                  <soft-ds-card-header>
                    <soft-ds-card-leading align="center">
                      <soft-ds-avatar
                        size="lg"
                        [initials]="
                          (
                            (c.firstName?.[0] ?? '') + (c.lastName?.[0] ?? '')
                          ).toUpperCase()
                        "
                      />
                    </soft-ds-card-leading>
                    <soft-ds-card-title>
                      {{ c.salutation }} {{ c.title }} {{ c.firstName }} {{ c.lastName }}
                    </soft-ds-card-title>
                    @if (c.position) {
                      <soft-ds-card-description>
                        {{ c.position }}
                      </soft-ds-card-description>
                    }
                    @if (c.mainContact) {
                      <soft-ds-card-trailing align="center">
                        <soft-ds-badge size="sm" variant="info">
                          Hauptkontakt
                        </soft-ds-badge>
                      </soft-ds-card-trailing>
                    }
                  </soft-ds-card-header>
                  <soft-ds-card-body>
                    <div class="flex flex-col gap-2.5 text-sm">
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                        @if (c.telephoneNumberOrganisation; as phone) {
                          <span class="inline-flex items-center gap-2 min-w-0">
                            <soft-ds-icon
                              name="fa-regular fa-phone"
                              size="xs"
                              color="secondary"
                            />
                            <a [href]="'tel:' + phone" class="soft-link truncate">
                              {{ phone }}
                            </a>
                          </span>
                        }
                        @if (c.mobilePhoneNumberOrganisation; as mobile) {
                          <span class="inline-flex items-center gap-2 min-w-0">
                            <soft-ds-icon
                              name="fa-regular fa-mobile"
                              size="xs"
                              color="secondary"
                            />
                            <a [href]="'tel:' + mobile" class="soft-link truncate">
                              {{ mobile }}
                            </a>
                          </span>
                        }
                        @if (c.faxOrganisation) {
                          <span class="inline-flex items-center gap-2 min-w-0">
                            <soft-ds-icon
                              name="fa-regular fa-fax"
                              size="xs"
                              color="secondary"
                            />
                            <span class="truncate">{{ c.faxOrganisation }}</span>
                          </span>
                        }
                      </div>
                      @if (c.mail1; as mail) {
                        <span class="inline-flex items-center gap-2 min-w-0 break-all">
                          <soft-ds-icon
                            name="fa-regular fa-at"
                            size="xs"
                            color="secondary"
                          />
                          <a [href]="'mailto:' + mail" class="soft-link">{{ mail }}</a>
                        </span>
                      }
                      @if (c.street || c.zipCode || c.location) {
                        <span class="inline-flex items-center gap-2">
                          <soft-ds-icon
                            name="fa-regular fa-location-dot"
                            size="xs"
                            color="secondary"
                          />
                          <span>
                            @if (c.street) {
                              {{ c.street }},
                            }
                            {{ c.zipCode }} {{ c.location }}
                          </span>
                        </span>
                      }
                    </div>
                  </soft-ds-card-body>
                </soft-ds-card>
              }
            </div>
          }
        }

        <!-- Stammdaten -->

        <!-- Konditionen -->
        @if (panelTabIndex() === 3) {
          @if (customerConditionsLoading()) {
            <p class="text-sm text-light">Lade Konditionen…</p>
          } @else if (customerConditionsError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Konditionen konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryLoad()"
            />
          } @else if (customerConditions().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              description="Keine Konditionen hinterlegt."
            />
          } @else {
            <div class="flex flex-col gap-2">
              @for (cs of customerConditions(); track cs.id) {
                <div class="flex flex-col gap-0.5 p-2 rounded-md hover:bg-hover">
                  <p class="ds-body-2-semibold">
                    {{ cs.conditionSheet?.name || cs.conditionSheet?.key || '—' }}
                  </p>
                  <div class="flex justify-between ds-caption text-light">
                    <span>
                      @if (cs.validFrom) {
                        {{ cs.validFrom | date: 'dd.MM.yyyy' }}
                      } @else {
                        —
                      }
                      –
                      @if (cs.validTo) {
                        {{ cs.validTo | date: 'dd.MM.yyyy' }}
                      } @else {
                        —
                      }
                    </span>
                    <span class="font-mono">#{{ cs.sequenceNumber }}</span>
                  </div>
                </div>
              }
            </div>
          }
        }

        <!-- Karten -->
        @if (panelTabIndex() === 4) {
          @if (customerCardsLoading()) {
            <p class="text-sm text-light">Lade Karten…</p>
          } @else if (customerCardsError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Karten konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryLoad()"
            />
          } @else if (customerCards().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              description="Keine Karten hinterlegt."
            />
          } @else {
            <div class="flex flex-col gap-4">
              @if (customerCards().length > 0) {
                <div class="flex flex-col gap-2">
                  <p class="sales-section-label">Kundenkarten</p>
                  @for (card of customerCards(); track card.id) {
                    <soft-ds-card>
                      <soft-ds-card-header>
                        <soft-ds-card-leading align="center">
                          <span class="sales-card-icon is-info">
                            <soft-ds-icon name="fa-regular fa-id-card" size="sm" />
                          </span>
                        </soft-ds-card-leading>
                        <soft-ds-card-title>
                          {{ card.customerCardType?.name || card.type || '—' }}
                        </soft-ds-card-title>
                        @if (card.key) {
                          <soft-ds-card-description>
                            {{ card.key }}
                          </soft-ds-card-description>
                        }
                        @if (card.blockedFrom) {
                          <soft-ds-card-trailing align="center">
                            <soft-ds-badge size="sm" variant="danger">
                              Gesperrt
                            </soft-ds-badge>
                          </soft-ds-card-trailing>
                        }
                      </soft-ds-card-header>
                      <soft-ds-card-body>
                        <div class="sales-row is-last">
                          <span class="sales-row__label">Gültig</span>
                          <span class="sales-row__value">
                            @if (card.validFrom) {
                              {{ card.validFrom | date: 'dd.MM.yyyy' }}
                            } @else {
                              —
                            }
                            –
                            @if (card.validUntil) {
                              {{ card.validUntil | date: 'dd.MM.yyyy' }}
                            } @else {
                              —
                            }
                          </span>
                        </div>
                      </soft-ds-card-body>
                    </soft-ds-card>
                  }
                </div>
              }
            </div>
          }
        }

        <!-- Tankkarten -->
        @if (panelTabIndex() === 5) {
          @if (customerFuelCardsLoading()) {
            <p class="text-sm text-light">Lade Tankkarten…</p>
          } @else if (customerFuelCardsError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Tankkarten konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryLoad()"
            />
          } @else if (customerFuelCards().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              description="Keine Tankkarten hinterlegt."
            />
          } @else {
            <div class="flex flex-col gap-4">
              @if (customerFuelCards().length > 0) {
                <div class="flex flex-col gap-2">
                  <p class="sales-section-label">Tankkarten</p>
                  @for (fc of customerFuelCards(); track fc.id) {
                    <soft-ds-card>
                      <soft-ds-card-header>
                        <soft-ds-card-leading align="center">
                          <span class="sales-card-icon is-warning">
                            <soft-ds-icon name="fa-regular fa-gas-pump" size="sm" />
                          </span>
                        </soft-ds-card-leading>
                        <soft-ds-card-title>
                          {{ fc.name || fc.type || '—' }}
                        </soft-ds-card-title>
                        @if (fc.key) {
                          <soft-ds-card-description>
                            {{ fc.key }}
                          </soft-ds-card-description>
                        }
                        <soft-ds-card-trailing align="center">
                          @if (fc.active) {
                            <soft-ds-badge size="sm" variant="success">
                              Aktiv
                            </soft-ds-badge>
                          } @else {
                            <soft-ds-badge size="sm" variant="secondary">
                              Inaktiv
                            </soft-ds-badge>
                          }
                        </soft-ds-card-trailing>
                      </soft-ds-card-header>
                      <soft-ds-card-body>
                        <div class="flex flex-col gap-1.5 text-sm">
                          @if (fc.type) {
                            <div class="sales-row">
                              <span class="sales-row__label">Treibstoff</span>
                              <span class="sales-row__value">{{ fc.type }}</span>
                            </div>
                          }
                          @if (fc.licensePlate) {
                            <div class="sales-row is-last">
                              <span class="sales-row__label">Kennzeichen</span>
                              <span class="sales-row__value">{{ fc.licensePlate }}</span>
                            </div>
                          }
                        </div>
                      </soft-ds-card-body>
                    </soft-ds-card>
                  }
                </div>
              }
            </div>
          }
        }

        <!-- Abholer -->
        @if (panelTabIndex() === 6) {
          @if (customerCollectorsLoading()) {
            <p class="text-sm text-light">Lade Abholer…</p>
          } @else if (customerCollectorsError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Abholer konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryLoad()"
            />
          } @else if (customerCollectors().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              description="Keine Abholer hinterlegt."
            />
          } @else {
            <div class="flex flex-col gap-2">
              @for (c of customerCollectors(); track c.id) {
                <soft-ds-item variant="outline" size="md">
                  <soft-ds-item-leading>
                    <span class="sales-card-icon is-info">
                      <soft-ds-icon name="fa-regular fa-truck-ramp-box" size="sm" />
                    </span>
                  </soft-ds-item-leading>
                  <soft-ds-item-title>{{ c.name }}</soft-ds-item-title>
                  @if (c.birthdate) {
                    <soft-ds-item-description>
                      <span class="inline-flex items-center gap-1.5">
                        <soft-ds-icon
                          name="fa-regular fa-cake-candles"
                          size="xs"
                          color="secondary"
                        />
                        <span>{{ c.birthdate | date: 'dd.MM.yyyy' }}</span>
                      </span>
                    </soft-ds-item-description>
                  }
                </soft-ds-item>
              }
            </div>
          }
        }

        <!-- Notizen -->
        @if (panelTabIndex() === 7) {
          @if (customerNotesLoading()) {
            <p class="text-sm text-light">Lade Notizen…</p>
          } @else if (customerNotesError()) {
            <soft-ds-empty-state
              variant="error"
              size="md"
              description="Notizen konnten nicht geladen werden."
              actionLabel="Erneut versuchen"
              (action)="retryLoad()"
            />
          } @else if (customerNotes().length === 0) {
            <soft-ds-empty-state
              variant="empty"
              size="md"
              description="Keine Notizen hinterlegt."
            />
          } @else {
            <div class="flex flex-col gap-2">
              @for (n of customerNotes(); track $index) {
                <soft-ds-card>
                  <soft-ds-card-header>
                    <soft-ds-card-leading align="center">
                      <span class="sales-card-icon">
                        <soft-ds-icon name="fa-regular fa-note-sticky" size="sm" />
                      </span>
                    </soft-ds-card-leading>
                    <soft-ds-card-title>
                      {{ n.category?.name || 'Notiz' }}
                    </soft-ds-card-title>
                    @if (n.date) {
                      <soft-ds-card-description>
                        {{ n.date | date: 'dd.MM.yyyy' }}
                      </soft-ds-card-description>
                    }
                  </soft-ds-card-header>
                  <soft-ds-card-body>
                    <p class="text-sm whitespace-pre-line">{{ n.note }}</p>
                    @if (n.user?.name) {
                      <p
                        class="ds-caption text-light mt-2 inline-flex items-center gap-1.5"
                      >
                        <soft-ds-icon name="fa-regular fa-user" size="xs" />
                        {{ n.user.name }}
                      </p>
                    }
                  </soft-ds-card-body>
                </soft-ds-card>
              }
            </div>
          }
        }
      </div>
    }
    <!-- end @else (loaded) -->
  </div>
  <!--div class="soft-customer-info-panel__footer shrink-0 border-t">
    <div class="flex items-center gap-2 w-full p-3">
      <soft-ds-button
        variant="outline"
        size="md"
        class="flex-1"
        (click)="openCustomerFile.emit()"
      >
        Kundenakte
      </soft-ds-button>
      <soft-ds-button
        variant="primary"
        size="md"
        class="flex-1"
        (click)="createDocument.emit()"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-plus" />
        </ng-template>
        Neuer Beleg
      </soft-ds-button>
    </div>
  </div-->
</div>
